import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Flex, useToasts } from '@applyboard/crystal-ui';

import { TranscriptSummarySection } from './TranscriptSummarySection';
import { SupportingDocumentsSection } from './SupportingDocumentsSection';
import { CoursesAndGradesSection } from './CoursesAndGradesSection';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { TranscriptDocument } from '~/types/transcriptExtraction';
import { EducationHistory } from '~/data/useApplication';
import { resolveAttributeValue } from '~/utils/transcriptExtraction';
import { formatDateToPopulateDatePicker } from '~/utils/formatDate';
import {
  EducationHistoryEditData,
  getChangedMetadata,
} from '../Cards/EducationHistory/components/EducationHistoryEditFields';
import { useUpdateEducationHistoryMetadata } from '../../data/useUpdateEducationHistory';
import { validateEducationHistory } from '../Cards/EducationHistory/educationHistory.validation';

function getLatestTranscript(docs: TranscriptDocument[]): TranscriptDocument | undefined {
  return docs.slice().sort((a, b) => {
    const getTime = (doc: TranscriptDocument) => {
      const { extractedOn, updatedAt, createdAt } = doc.attributes;
      return new Date(extractedOn ?? updatedAt ?? createdAt ?? 0).getTime();
    };
    return getTime(b) - getTime(a);
  })[0];
}

function sortEducationHistories(items: EducationHistory[]): EducationHistory[] {
  return [...items].sort((a, b) => {
    const aTime = a.endDate ? new Date(a.endDate).getTime() : -Infinity;
    const bTime = b.endDate ? new Date(b.endDate).getTime() : -Infinity;
    return bTime - aTime;
  });
}

/** Build initial edit data from education history item and transcript */
function buildEditData(item: EducationHistory, transcript?: TranscriptDocument): EducationHistoryEditData {
  const attrs = transcript?.attributes;
  return {
    schoolName: (attrs && resolveAttributeValue(attrs.schoolName)) || item.schoolName || '',
    educationLevel: item.level.code || '',
    educationCountry: item.country.alpha2Code || '',
    credentialReceived: (attrs && resolveAttributeValue(attrs.credentialReceived)) || item.credentialReceived || '',
    fieldOfStudy: (attrs && resolveAttributeValue(attrs.fieldOfStudy)) || '',
    degree: (attrs && resolveAttributeValue(attrs.degree)) || '',
    gradeSchemeId: (attrs && resolveAttributeValue(attrs.gradeSchemeId)) || '',
    gradeSchemeName: (attrs && resolveAttributeValue(attrs.gradeSchemeName)) || '',
    startDate: formatDateToPopulateDatePicker(item.startDate),
    endDate: formatDateToPopulateDatePicker(item.endDate),
  };
}

export function EvaluationTab() {
  const {
    application: { educationHistories, studentPersonaInformation, id: applicationId },
  } = useAppProcessing();

  const sortedItems = React.useMemo(
    () => sortEducationHistories(educationHistories.educationHistoryItems ?? []),
    [educationHistories.educationHistoryItems]
  );

  return (
    <Flex direction="column" gap={8}>
      {sortedItems.map((item) => {
        const transcripts = (item.transcriptExtractions ?? []) as TranscriptDocument[];
        const latestTranscript = getLatestTranscript(transcripts) ?? transcripts[0];

        return (
          <EvaluationEducationHistoryItem
            key={item.key}
            item={item}
            latestTranscript={latestTranscript}
            studentId={studentPersonaInformation?.id}
            applicationId={applicationId}
          />
        );
      })}
    </Flex>
  );
}

type EvaluationEducationHistoryItemProps = Readonly<{
  item: EducationHistory;
  latestTranscript?: TranscriptDocument;
  studentId?: string;
  applicationId: string;
}>;

/** Per-item wrapper that manages edit state for the TranscriptSummarySection */
function EvaluationEducationHistoryItem({
  item,
  latestTranscript,
  studentId,
  applicationId,
}: EvaluationEducationHistoryItemProps) {
  const toast = useToasts();
  const { updateEducationHistoryMetadata, isUpdatingEducationHistoryMetadata } = useUpdateEducationHistoryMetadata();

  const originalData = useRef<EducationHistoryEditData>(buildEditData(item, latestTranscript));
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<EducationHistoryEditData>(() => buildEditData(item, latestTranscript));

  // Sync props to state when not editing
  useEffect(() => {
    if (!isEditing) {
      const fresh = buildEditData(item, latestTranscript);
      originalData.current = fresh;
      setEditedData(fresh);
    }
  }, [item, isEditing, latestTranscript]);

  const handleFieldChange = useCallback((field: keyof EducationHistoryEditData, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    setEditedData(buildEditData(item, latestTranscript));
    setIsEditing(false);
  }, [item, latestTranscript]);

  const handleSave = useCallback(async () => {
    const validation = validateEducationHistory(editedData);
    if (!validation.isValid && validation.errors) {
      validation.errors.forEach((error) => {
        toast.negative(new Error(error));
      });
      return;
    }

    if (!studentId) {
      toast.negative(new Error('Student ID is not available'));
      return;
    }

    const changedMetadata = getChangedMetadata(originalData.current, editedData);
    if (!changedMetadata) {
      // Nothing changed — just exit edit mode
      setIsEditing(false);
      return;
    }

    const educationHistoryId = latestTranscript?.id ?? item.key;

    try {
      await updateEducationHistoryMetadata({
        studentId,
        educationHistoryId,
        applicationId,
        educationHistoryKey: item.key,
        metadata: changedMetadata,
      });
      toast.positive('Education history updated successfully');
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update education history';
      toast.negative(new Error(errorMessage));
    }
  }, [
    editedData,
    studentId,
    latestTranscript,
    item.key,
    applicationId,
    toast,
    updateEducationHistoryMetadata,
    originalData,
  ]);

  return (
    <div style={{ border: '1px solid #E4E7EC', borderRadius: 8, background: 'white', padding: 20 }}>
      <Flex direction="column" gap={6}>
        <TranscriptSummarySection
          educationHistoryItem={item}
          transcriptDocument={latestTranscript}
          isEditing={isEditing}
          editedData={editedData}
          isSaving={isUpdatingEducationHistoryMetadata}
          onEdit={() => setIsEditing(true)}
          onChange={handleFieldChange}
          onSave={handleSave}
          onCancel={handleCancel}
        />
        <SupportingDocumentsSection educationHistoryItem={item} />
        <CoursesAndGradesSection educationHistoryItems={[item]} />
      </Flex>
    </div>
  );
}
